/**
 * assetIndex — the Media Library's Phase-2 layer: named assets + version history,
 * stored as `ydoc.getMap("assets")` so it rides the SAME provider as the doc
 * (cross-tab, cross-peer, offline-first — no server). See docs/plans/2026-07-22-media-library.md.
 *
 * Design choice (lower-risk than a cross-cutting resolver, plan §7 Q3): widgets
 * still bind by `contentProps.sha256`. "Replace everywhere" is a grid REWRITE
 * (rewriteImageSha) plus an index update, NOT an indirection — so render /
 * publish / export / scan are untouched. The index only adds names, the version
 * chain, and the "which sha is current" pointer that names survive across.
 *
 * Entries are LAZY: created only when a user names or replaces an asset, so
 * opening the library never mutates the doc. History shas are hidden from the
 * catalog (they're versions, not orphans) and their bytes stay in OPFS.
 */
import { useEffect, useState } from "react";
import * as Y from "yjs";

const ASSETS_MAP = "assets";

/** A prior version: the sha PLUS its own mime/ext, so rolling back restores the
 *  correct content type (the current tile's metadata would be wrong after a
 *  cross-format replace, e.g. png → webp, or worse image → video). */
export interface VersionRef {
  readonly sha: string;
  readonly mime?: string;
  readonly ext?: string;
}

export interface AssetEntry {
  readonly id: string;
  readonly name: string;
  readonly currentSha: string;
  /** Older versions, newest-last; the current version is NOT included. */
  readonly history: readonly VersionRef[];
  readonly mime?: string;
  readonly ext?: string;
  readonly createdAt: number;
}

interface Meta {
  readonly mime?: string;
  readonly ext?: string;
}

function assetsMap(ydoc: Y.Doc): Y.Map<AssetEntry> {
  return ydoc.getMap<AssetEntry>(ASSETS_MAP);
}

export function readIndex(ydoc: Y.Doc): AssetEntry[] {
  return [...assetsMap(ydoc).values()];
}

/** The entry whose CURRENT version is this sha, if any. */
export function entryByCurrentSha(
  ydoc: Y.Doc,
  sha: string,
): AssetEntry | undefined {
  for (const e of assetsMap(ydoc).values()) if (e.currentSha === sha) return e;
  return undefined;
}

/** Every sha that is a historical (non-current) version of some named asset —
 *  hidden from the catalog so old versions don't read as orphans. */
export function historyShas(ydoc: Y.Doc): Set<string> {
  const s = new Set<string>();
  for (const e of assetsMap(ydoc).values())
    for (const h of e.history) s.add(h.sha);
  return s;
}

/** Find-or-create a named entry whose current version is `sha`. A sha that is
 *  already some asset's history version belongs to THAT asset (returned). */
export function ensureEntry(ydoc: Y.Doc, sha: string, meta?: Meta): AssetEntry {
  const cur = entryByCurrentSha(ydoc, sha);
  if (cur) return cur;
  for (const e of assetsMap(ydoc).values())
    if (e.history.some((h) => h.sha === sha)) return e;
  const entry: AssetEntry = {
    id: crypto.randomUUID(),
    name: "",
    currentSha: sha,
    history: [],
    mime: meta?.mime,
    ext: meta?.ext,
    createdAt: nowMs(),
  };
  assetsMap(ydoc).set(entry.id, entry);
  return entry;
}

/** Drop a named entry entirely (name + version chain). The caller deletes the
 *  underlying bytes (current + history) from OPFS separately — this only removes
 *  the index record so the asset stops surfacing in the catalog. Idempotent. */
export function removeEntry(ydoc: Y.Doc, id: string): void {
  assetsMap(ydoc).delete(id);
}

export function renameAsset(ydoc: Y.Doc, id: string, name: string): void {
  const e = assetsMap(ydoc).get(id);
  if (!e || e.name === name) return;
  assetsMap(ydoc).set(id, { ...e, name });
}

/**
 * Advance an asset to a new current version: the old current drops onto history.
 * Pure index op — the caller REWRITES the grids (rewriteImageSha) separately.
 */
export function pushVersion(
  ydoc: Y.Doc,
  id: string,
  newSha: string,
  meta?: Meta,
): void {
  const e = assetsMap(ydoc).get(id);
  if (!e || e.currentSha === newSha) return;
  assetsMap(ydoc).set(id, {
    ...e,
    currentSha: newSha,
    // Archive the OUTGOING version with ITS OWN mime/ext.
    history: [...e.history, { sha: e.currentSha, mime: e.mime, ext: e.ext }],
    mime: meta?.mime ?? e.mime,
    ext: meta?.ext ?? e.ext,
  });
}

/** Roll the current version back to one of the history versions, restoring that
 *  version's mime/ext (not the current tile's). */
export function rollbackTo(ydoc: Y.Doc, id: string, sha: string): void {
  const e = assetsMap(ydoc).get(id);
  const target = e?.history.find((h) => h.sha === sha);
  if (!e || !target) return;
  assetsMap(ydoc).set(id, {
    ...e,
    currentSha: target.sha,
    mime: target.mime,
    ext: target.ext,
    history: [
      ...e.history.filter((h) => h.sha !== sha),
      { sha: e.currentSha, mime: e.mime, ext: e.ext },
    ],
  });
}

/** Reactive view of the index (re-renders on any asset change). */
export function useAssetIndex(ydoc: Y.Doc | null): AssetEntry[] {
  const [entries, setEntries] = useState<AssetEntry[]>(() =>
    ydoc ? readIndex(ydoc) : [],
  );
  useEffect(() => {
    if (!ydoc) {
      setEntries([]);
      return;
    }
    const m = assetsMap(ydoc);
    const update = () => setEntries(readIndex(ydoc));
    update();
    m.observe(update);
    return () => m.unobserve(update);
  }, [ydoc]);
  return entries;
}

// Isolated so tests can assert ordering without a clock; real callers get Date.
function nowMs(): number {
  return Date.now();
}
